import evaluateFieldConditions from '../evaluate-field-conditions';

/**
 * Clear values from fields that are hidden due to conditional rendering
 * (but NOT fields of type "hidden" - those should keep their values)
 *
 * This should be called before form submission to prevent conflicting values
 * when users change options that hide previously visible fields.
 *
 * Follows the same pattern as prepareValuesForSubmission in candidate_application/form-page-helpers.js
 */
export const clearHiddenFieldValues = (
  fields,
  getValues,
  setValue,
  formValuesPrefix = '',
  context = {},
) => {
  if (!fields || !Array.isArray(fields) || fields.length === 0) {
    return [];
  }

  // Get current form values without watching (to avoid infinite loops)
  const currentValues = getValues(formValuesPrefix) || {};

  // Extract context values for condition evaluation
  const {
    company = null,
    enabledCompanyChecks,
    companyCheckConfigurations = null,
    companyCheckConfig = null,
    candidate = null,
    candidateValues = null,
    checkDetails = null,
    subCheckDetails = null,
    selectedOptionsContext = {},
  } = context;

  // Build context for condition evaluation
  const contextForEvaluation = {
    company,
    enabledCompanyChecks,
    companyCheckConfigurations,
    companyCheckConfig,
    candidate,
    candidateValues,
    checkDetails,
    subCheckDetails,
    selectedOptionsContext,
  };

  // Identify fields that should be cleared:
  // - Fields that are NOT visible due to conditions (conditionsMet = false)
  // - Fields that are NOT of type "hidden" (hidden fields keep their values)
  // - Fields that have a value in the form
  // This follows the same pattern as prepareValuesForSubmission in candidate_application/form-page-helpers.js
  const fieldsToClear = [];
  for (const field of fields) {
    // Skip fields of type "hidden" - they should keep their values
    if (field.type === 'hidden') continue;

    // Check if field should be visible based on conditions
    // This matches the pattern in prepareValuesForSubmission: processField function
    let conditionsMet = true;
    if (field.conditions) {
      try {
        conditionsMet = evaluateFieldConditions(
          field.conditions,
          currentValues,
          contextForEvaluation,
        );
      } catch (error) {
        console.error('Error evaluating field conditions in clearHiddenFieldValues:', error, field);
        // If condition evaluation fails, assume visible to avoid clearing incorrectly
        conditionsMet = true;
      }
    }

    // If conditions not met, clear the field value
    // This matches prepareValuesForSubmission: "If conditions not met, delete from cleanedWatchValues"
    if (!conditionsMet) {
      // Build the form field path using the field code
      const fieldPath = formValuesPrefix ? `${formValuesPrefix}.${field.code}` : field.code;

      const currentValue = getValues(fieldPath);

      // Only clear if there's actually a value (handle arrays, objects, strings, etc.)
      const hasValue =
        currentValue !== undefined &&
        currentValue !== null &&
        currentValue !== '' &&
        !(Array.isArray(currentValue) && currentValue.length === 0) &&
        !(typeof currentValue === 'object' && Object.keys(currentValue).length === 0);

      if (hasValue) {
        fieldsToClear.push(fieldPath);
      }

      // Also clear nested fields (fields that start with field.code.)
      // This handles subFields and array items, matching FormPageRenderer pattern
      const allFormValues = getValues();
      Object.keys(allFormValues || {}).forEach(key => {
        // Check if this key matches the field pattern
        // If formValuesPrefix exists, key should be within that scope
        const baseKey =
          formValuesPrefix && key.startsWith(`${formValuesPrefix}.`)
            ? key.substring(formValuesPrefix.length + 1) // Remove prefix and dot
            : key;

        // Match pattern: field.code or field.code.something (for nested fields)
        const matchesFieldPattern = baseKey === field.code || baseKey.startsWith(`${field.code}.`);

        if (matchesFieldPattern && key !== fieldPath) {
          const nestedValue = getValues(key);
          const hasNestedValue =
            nestedValue !== undefined &&
            nestedValue !== null &&
            nestedValue !== '' &&
            !(Array.isArray(nestedValue) && nestedValue.length === 0) &&
            !(typeof nestedValue === 'object' && Object.keys(nestedValue).length === 0);

          if (hasNestedValue) {
            fieldsToClear.push(key);
          }
        }
      });
    }
  }

  // Clear the values (using setValue without triggering validation)
  // This matches the pattern in prepareValuesForSubmission where hidden fields are deleted
  // Use undefined instead of '' to properly clear arrays and objects
  fieldsToClear.forEach(fieldPath => {
    setValue(fieldPath, undefined, { shouldValidate: false, shouldDirty: false });
  });

  return fieldsToClear;
};
